import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import {
  Calendar, Clock, Users, User as UserIcon, ListTodo, Layers,
  Mail, Hash, CheckCircle2, AlertCircle, Crown,
} from "lucide-react";

function fmt(d, withTime = false) {
  if (!d) return "—";
  try {
    const date = new Date(d);
    const opts = withTime
      ? { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }
      : { day: "2-digit", month: "long", year: "numeric" };
    return date.toLocaleString("es-MX", opts);
  } catch {
    return d;
  }
}

function relativeTime(d) {
  if (!d) return "";
  const date = new Date(d);
  const diff = Date.now() - date.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "hace instantes";
  if (min < 60) return `hace ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `hace ${hr} h`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `hace ${days} día${days === 1 ? "" : "s"}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `hace ${months} mes${months === 1 ? "" : "es"}`;
  const years = Math.floor(months / 12);
  return `hace ${years} año${years === 1 ? "" : "s"}`;
}

function initials(name = "") {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";
}

export default function BoardDetailsDialog({ boardId, open, onOpenChange }) {
  const { data, isLoading } = useQuery({
    queryKey: ["board-details", boardId],
    queryFn: async () => (await api.get(`/boards/${boardId}/details`)).data,
    enabled: !!open && !!boardId,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-hidden p-0 border-0 bg-transparent shadow-none"
        data-testid="board-details-dialog"
      >
        {/* Glass wrapper with shine + glow */}
        <div className="relative rounded-2xl overflow-hidden">
          {/* Ambient blue glow behind the dialog */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-[1px] rounded-2xl"
            style={{
              background:
                "radial-gradient(120% 80% at 0% 0%, rgba(96,165,250,0.35) 0%, transparent 50%), radial-gradient(120% 80% at 100% 100%, rgba(59,130,246,0.25) 0%, transparent 55%)",
              filter: "blur(20px)",
              opacity: 0.7,
            }}
          />

          {/* Main glass surface */}
          <div
            className="relative rounded-2xl bg-white/40 dark:bg-slate-900/40 backdrop-blur-2xl backdrop-saturate-150 border border-white/60 dark:border-white/10 shadow-[0_25px_80px_-20px_rgba(30,64,175,0.45),0_8px_32px_-8px_rgba(0,0,0,0.18)] overflow-hidden"
          >
            {/* Top shine reflection */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.9) 50%, transparent 100%)",
              }}
            />
            {/* Diagonal glossy highlight */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-2xl opacity-50 dark:opacity-25"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,0) 65%, rgba(255,255,255,0.15) 100%)",
              }}
            />
            {/* Inner content with its own scroll */}
            <div className="relative max-h-[85vh] overflow-y-auto p-6">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl flex items-center gap-2">
                  <span className="grid place-items-center h-8 w-8 rounded-lg bg-gradient-to-br from-blue-400/30 to-blue-600/20 border border-white/40 dark:border-white/10 shadow-inner">
                    <Hash className="h-4 w-4 text-blue-500" />
                  </span>
                  Detalles del tablero
                </DialogTitle>
                <DialogDescription>
                  Ficha resumen con autor, integrantes, métricas y última actualización.
                </DialogDescription>
              </DialogHeader>

        {isLoading || !data ? (
          <div className="space-y-3 py-2">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <div className="space-y-6 py-1" data-testid="board-details-content">
            {/* Title + status */}
            <section>
              <h3
                className="font-display text-xl font-semibold leading-tight"
                data-testid="board-details-title"
              >
                {data.name}
              </h3>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {data.is_collaborative ? (
                  <Badge
                    variant="secondary"
                    className="bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30"
                    data-testid="board-details-collaborative-badge"
                  >
                    <Users className="h-3 w-3 mr-1" />
                    Colaborativo · {data.members_count}
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="bg-white/40 dark:bg-white/5"
                    data-testid="board-details-private-badge"
                  >
                    <UserIcon className="h-3 w-3 mr-1" />
                    Privado
                  </Badge>
                )}
                {data.is_archived && (
                  <Badge variant="destructive" className="bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30">
                    Archivado
                  </Badge>
                )}
                {data.is_starred && (
                  <Badge variant="outline" className="bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-500/30">
                    Destacado
                  </Badge>
                )}
              </div>
            </section>

            {/* Description */}
            <section>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                Descripción
              </p>
              <p
                className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap"
                data-testid="board-details-description"
              >
                {data.description?.trim() || (
                  <span className="text-muted-foreground italic">Sin descripción</span>
                )}
              </p>
            </section>

            {/* Stats grid */}
            <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard
                icon={ListTodo}
                label="Tareas"
                value={data.cards_count}
                testId="board-details-cards-count"
              />
              <StatCard
                icon={Layers}
                label="Listas"
                value={data.lists_count}
                testId="board-details-lists-count"
              />
              <StatCard
                icon={Users}
                label="Integrantes"
                value={data.members_count}
                testId="board-details-members-count"
              />
              <StatCard
                icon={Clock}
                label="Actualizado"
                value={relativeTime(data.updated_at) || "—"}
                small
                testId="board-details-updated-rel"
              />
            </section>

            {/* Author + dates */}
            <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2 flex items-center gap-1">
                  <UserIcon className="h-3 w-3" /> Creador
                </p>
                <div className="flex items-center gap-3" data-testid="board-details-author">
                  <div className="relative">
                    <Avatar className="h-12 w-12 ring-2 ring-amber-400/60 ring-offset-2 ring-offset-transparent">
                      <AvatarImage src={data.author?.picture} alt={data.author?.name} />
                      <AvatarFallback className="bg-blue-500/20 text-blue-700 dark:text-blue-300 text-sm">
                        {initials(data.author?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 grid place-items-center shadow-md" title="Creador del tablero">
                      <Crown className="h-3 w-3 text-white" />
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate flex items-center gap-1.5">
                      {data.author?.name || "—"}
                      <span className="text-[9px] uppercase tracking-wider font-semibold bg-amber-500/20 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded">
                        Creador
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{data.author?.email || ""}</p>
                    {typeof data.author?.assigned_cards_count === "number" && data.author.assigned_cards_count > 0 && (
                      <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="board-details-author-cards">
                        <ListTodo className="h-3 w-3 inline mr-1" />
                        {data.author.assigned_cards_count} tarea{data.author.assigned_cards_count === 1 ? "" : "s"} asignada{data.author.assigned_cards_count === 1 ? "" : "s"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Fechas
                </p>
                <dl className="text-sm space-y-1">
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">Creado:</dt>
                    <dd className="text-right" data-testid="board-details-created-at">{fmt(data.created_at)}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">Última act.:</dt>
                    <dd className="text-right" data-testid="board-details-updated-at">{fmt(data.updated_at, true)}</dd>
                  </div>
                </dl>
              </div>
            </section>

            {/* Members list */}
            {data.is_collaborative && (
              <section>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3 flex items-center gap-1">
                  <Users className="h-3 w-3" /> Integrantes ({data.members.length})
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="board-details-members-list">
                  {data.members.map((m, idx) => (
                    <li
                      key={m.user_id || m.email || idx}
                      className="relative flex items-center gap-3 p-3 rounded-xl bg-gradient-to-br from-white/55 to-white/25 dark:from-white/10 dark:to-white/[0.03] border border-white/60 dark:border-white/10 backdrop-blur-md shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] hover:shadow-[0_6px_18px_-6px_rgba(59,130,246,0.25)] transition-shadow"
                      data-testid={`board-details-member-${idx}`}
                    >
                      <Avatar className="h-11 w-11 ring-2 ring-white/70 dark:ring-white/10">
                        <AvatarImage src={m.picture} alt={m.name} />
                        <AvatarFallback className="bg-blue-500/20 text-blue-700 dark:text-blue-300 text-sm">
                          {initials(m.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{m.name}</p>
                        <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                          <Mail className="h-3 w-3" /> {m.email || "—"}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {m.status === "active" ? (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded">
                              <CheckCircle2 className="h-2.5 w-2.5" /> Activo
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-amber-500/15 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded">
                              <AlertCircle className="h-2.5 w-2.5" /> Pendiente
                            </span>
                          )}
                          {typeof m.assigned_cards_count === "number" && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground"
                              data-testid={`board-details-member-cards-${idx}`}
                              title="Tareas asignadas a este integrante"
                            >
                              <ListTodo className="h-2.5 w-2.5" />
                              {m.assigned_cards_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            </div>
            )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ icon: Icon, label, value, small, testId }) {
  return (
    <div
      className="relative rounded-xl p-3 bg-gradient-to-br from-white/60 to-white/25 dark:from-white/10 dark:to-white/[0.03] border border-white/60 dark:border-white/10 backdrop-blur-md shadow-[0_4px_14px_-6px_rgba(15,23,42,0.18),inset_0_1px_0_rgba(255,255,255,0.6)] dark:shadow-[0_4px_14px_-6px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.08)] overflow-hidden"
      data-testid={testId}
    >
      {/* Subtle top shine */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-2 top-0 h-px bg-gradient-to-r from-transparent via-white/80 dark:via-white/30 to-transparent"
      />
      <div className="relative flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        <Icon className="h-3.5 w-3.5 text-blue-500/80" />
        <span className="font-medium">{label}</span>
      </div>
      <p className={small ? "relative text-sm font-semibold" : "relative text-2xl font-display font-semibold"}>
        {value}
      </p>
    </div>
  );
}
